import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} productImage
 */

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  // ── AJOUT : état des selects custom ───────────────────────────────────
  /** @type {HTMLElement | null} */
  #selectsWrapper = null;

  /** @type {string | null} */
  #openSelectId = null;
  // ───────────────────────────────────────────────────────────────────────

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // ── AJOUT ──────────────────────────────────────────────────────────
    requestAnimationFrame(() => this.#buildSelects());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
  }

  // ── AJOUT : construction des selects custom ────────────────────────────

  /**
   * Lit les fieldsets du variant-picker et construit un select custom par option.
   * Gère 1, 2 ou 3 dimensions (Taille × Couleur × etc.)
   */
  #buildSelects() {
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return;

    const fieldsets = variantPicker.querySelectorAll('fieldset[ref="fieldsets[]"]');
    if (!fieldsets.length) return;

    const variantTitleEl = this.querySelector('.sticky-add-to-cart__variant');
    if (!variantTitleEl) return;

    // Wrapper qui contiendra tous les selects côte à côte
    const wrapper = document.createElement('div');
    wrapper.className = 'sticky-custom-selects';

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const legend = fieldset.querySelector('legend');
      const label = legend?.textContent?.trim() || `Option ${fieldsetIndex + 1}`;
      const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
        fieldset.querySelectorAll('input[type="radio"]')
      );

      // Moins de 2 options dans ce fieldset → texte simple
      if (radios.length < 2) {
        const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
        if (checkedRadio) {
          const span = document.createElement('span');
          span.className = 'sticky-custom-select__single';
          span.textContent = /** @type {HTMLInputElement} */ (checkedRadio).value;
          wrapper.appendChild(span);
        }
        return;
      }

      const checkedRadio = /** @type {HTMLInputElement | null} */ (
        fieldset.querySelector('input[type="radio"]:checked')
      );

      const selectId = `sticky-select-${fieldsetIndex}`;

      // Wrapper du select individuel
      const customSelect = document.createElement('div');
      customSelect.className = 'sticky-custom-select';
      customSelect.dataset.selectId = selectId;
      customSelect.dataset.fieldsetIndex = String(fieldsetIndex);
      customSelect.setAttribute('role', 'combobox');
      customSelect.setAttribute('aria-expanded', 'false');
      customSelect.setAttribute('aria-haspopup', 'listbox');
      customSelect.setAttribute('aria-label', label);

      // Trigger
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'sticky-custom-select__trigger';
      trigger.innerHTML = `
        <span class="sticky-custom-select__value">${checkedRadio?.value || ''}</span>
        <span class="sticky-custom-select__arrow" aria-hidden="true">
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      `;

      // Dropdown — s'ouvre vers le HAUT
      const dropdown = document.createElement('ul');
      dropdown.className = 'sticky-custom-select__dropdown';
      dropdown.setAttribute('role', 'listbox');
      dropdown.setAttribute('aria-label', label);

      radios.forEach((radio) => {
        const li = document.createElement('li');
        li.className = 'sticky-custom-select__option';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', radio.checked ? 'true' : 'false');
        li.dataset.value = radio.value;
        li.dataset.fieldsetIndex = String(fieldsetIndex);
        li.textContent = radio.value;
        if (radio.checked) li.classList.add('is-selected');

        // Disponibilité
        if (radio.dataset.optionAvailable === 'false') {
          li.classList.add('is-unavailable');
          li.setAttribute('aria-disabled', 'true');
        }

        dropdown.appendChild(li);
      });

      customSelect.appendChild(dropdown); // dropdown AVANT le trigger (au-dessus dans le DOM pour z-index)
      customSelect.appendChild(trigger);

      // Événements du trigger
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        this.#toggleSelect(selectId, customSelect);
      }, { signal: this.#abortController.signal });

      // Événements des options
      dropdown.addEventListener('click', (e) => {
        const option = /** @type {HTMLElement} */ (e.target)?.closest('.sticky-custom-select__option');
        if (!option || option.classList.contains('is-unavailable')) return;
        this.#selectOption(
          customSelect,
          option.dataset.value || '',
          parseInt(option.dataset.fieldsetIndex || '0')
        );
      }, { signal: this.#abortController.signal });

      wrapper.appendChild(customSelect);
    });

    // Fermer au clic en dehors
    document.addEventListener('click', (e) => {
      if (this.#openSelectId && !wrapper.contains(/** @type {Node} */ (e.target))) {
        this.#closeAllSelects();
      }
    }, { signal: this.#abortController.signal });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#openSelectId) this.#closeAllSelects();
    }, { signal: this.#abortController.signal });

    variantTitleEl.textContent = '';
    variantTitleEl.appendChild(wrapper);
    this.#selectsWrapper = wrapper;
  }

  /**
   * Ouvre/ferme un select, ferme les autres
   * @param {string} selectId
   * @param {HTMLElement} customSelect
   */
  #toggleSelect(selectId, customSelect) {
    if (this.#openSelectId === selectId) {
      this.#closeAllSelects();
    } else {
      this.#closeAllSelects();
      this.#openSelectId = selectId;
      customSelect.setAttribute('aria-expanded', 'true');
      customSelect.classList.add('is-open');
    }
  }

  #closeAllSelects() {
    if (!this.#selectsWrapper) return;
    this.#selectsWrapper.querySelectorAll('.sticky-custom-select.is-open').forEach((el) => {
      el.classList.remove('is-open');
      el.setAttribute('aria-expanded', 'false');
    });
    this.#openSelectId = null;
  }

  /**
   * Sélectionne une valeur dans un fieldset et clique le radio correspondant
   * @param {HTMLElement} customSelect
   * @param {string} value
   * @param {number} fieldsetIndex
   */
  #selectOption(customSelect, value, fieldsetIndex) {
    // Met à jour l'affichage du trigger
    const valueEl = customSelect.querySelector('.sticky-custom-select__value');
    if (valueEl) valueEl.textContent = value;

    // Met à jour les options du dropdown
    customSelect.querySelectorAll('.sticky-custom-select__option').forEach((opt) => {
      const el = /** @type {HTMLElement} */ (opt);
      const isSelected = el.dataset.value === value;
      el.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      el.classList.toggle('is-selected', isSelected);
    });

    this.#closeAllSelects();

    // Clique le radio correspondant dans le variant-picker → le thème gère tout
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return;

    const fieldsets = variantPicker.querySelectorAll('fieldset[ref="fieldsets[]"]');
    const targetFieldset = fieldsets[fieldsetIndex];
    if (!targetFieldset) return;

    const targetRadio = /** @type {HTMLInputElement | null} */ (
      targetFieldset.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`)
    );
    if (targetRadio && !targetRadio.checked) targetRadio.click();
  }

  /**
   * Synchronise tous les selects avec l'état actuel du variant-picker
   */
  #syncSelects() {
    if (!this.#selectsWrapper) return;
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return;

    const fieldsets = variantPicker.querySelectorAll('fieldset[ref="fieldsets[]"]');
    const customSelects = this.#selectsWrapper.querySelectorAll('.sticky-custom-select');

    customSelects.forEach((customSelect, index) => {
      const fieldset = fieldsets[index];
      if (!fieldset) return;

      const checkedRadio = /** @type {HTMLInputElement | null} */ (
        fieldset.querySelector('input[type="radio"]:checked')
      );
      if (!checkedRadio) return;

      const value = checkedRadio.value;
      const valueEl = customSelect.querySelector('.sticky-custom-select__value');
      if (valueEl) valueEl.textContent = value;

      customSelect.querySelectorAll('.sticky-custom-select__option').forEach((opt) => {
        const el = /** @type {HTMLElement} */ (opt);
        const isSelected = el.dataset.value === value;
        el.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        el.classList.toggle('is-selected', isSelected);
      });
    });
  }

  /** @returns {Element | null} */
  #getVariantPicker() {
    const productId = this.dataset.productId;
    if (!productId) return null;
    return document.querySelector(`variant-picker[data-product-id="${productId}"]`);
  }

  // ───────────────────────────────────────────────────────────────────────

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) this.#showStickyBar();
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant?.id) this.dataset.currentVariantId = variant.id;

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) this.#handleVariantUnavailable();

    this.#updateButtonText();

    // ── AJOUT : reconstruit les selects après le morph ─────────────────
    requestAnimationFrame(() => {
      this.#selectsWrapper = null;
      this.#openSelectId = null;
      this.#buildSelects();
    });
  };

  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
    // ── AJOUT : synchronise les selects ──────────────────────────────
    this.#syncSelects();
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;
    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const available = !addToCartButton.disabled;
    quantityNumber.textContent = this.#currentQuantity.toString();
    quantityDisplay.style.display = available && this.#currentQuantity > 1 ? 'inline' : 'none';
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
